import React, { useMemo, useState } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/ui/accordion';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { handleApiError } from '@/shared/api/requests';
import { useToast } from '@/shared/hooks/use-toast';

import {
  useProjectMemberships,
  useProjectPermissions,
  useProjectRoles,
  useProjectUsers,
  useRolesMutations,
} from '../hooks';
import type { ProjectRole, RoleInput } from '../types';
import { groupPermissionsByFeature } from '../utils/groupPermissions';
import { MembershipAssignmentCard } from './MembershipAssignmentCard';
import { PermissionRequestsCard } from './PermissionRequestsCard';
import { RoleDetailsDialog } from './RoleDetailsDialog';
import { RoleFormDialog } from './RoleFormDialog';
import { RolesTable } from './RolesTable';

interface ProjectRbacPanelProps {
  projectId: number | undefined;
}

/**
 * Self-contained RBAC management surface for a single project.
 *
 * Renders the roles table, membership assignment UI, role form dialog,
 * and a read-only permission catalog. Designed to be embedded inside
 * other project-scoped pages (currently the Project Team page) so the
 * RBAC controls live where teammates already manage people.
 */
export const ProjectRbacPanel: React.FC<ProjectRbacPanelProps> = ({ projectId }) => {
  const { toast } = useToast();
  const [editingRole, setEditingRole] = useState<ProjectRole | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewingRole, setViewingRole] = useState<ProjectRole | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const { data: permissions = [], isLoading: permissionsLoading } = useProjectPermissions(projectId);
  const { data: roles = [], isLoading: rolesLoading } = useProjectRoles(projectId);
  const { data: memberships = [], isLoading: membershipsLoading } = useProjectMemberships(projectId);
  const { data: users = [], isLoading: usersLoading } = useProjectUsers(projectId);
  const { createRole, updateRole, deleteRole, upsertMembership } = useRolesMutations(projectId);

  const isLoadingData = permissionsLoading || rolesLoading || membershipsLoading || usersLoading;

  const permissionGroups = useMemo(
    () => groupPermissionsByFeature(permissions),
    [permissions],
  );

  const openCreateDialog = () => {
    setEditingRole(null);
    setDialogOpen(true);
  };

  const openEditDialog = (role: ProjectRole) => {
    setEditingRole(role);
    setDialogOpen(true);
  };

  const openDetailsDialog = (role: ProjectRole) => {
    setViewingRole(role);
    setDetailsDialogOpen(true);
  };

  const handleSaveRole = async (payload: RoleInput) => {
    try {
      if (editingRole) {
        await updateRole.mutateAsync({ roleId: editingRole.id, payload });
        toast({ title: 'Role updated successfully.' });
      } else {
        await createRole.mutateAsync(payload);
        toast({ title: 'Role created successfully.' });
      }
      setDialogOpen(false);
      setEditingRole(null);
    } catch (error) {
      toast({
        title: 'Unable to save role',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteRole = async (role: ProjectRole) => {
    try {
      await deleteRole.mutateAsync(role.id);
      toast({ title: `${role.name} deleted.` });
    } catch (error) {
      toast({
        title: 'Unable to delete role',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  const handleAssignMembership = async (payload: { user_id: number; role_id: number }) => {
    try {
      await upsertMembership.mutateAsync(payload);
      toast({ title: 'Membership updated.' });
    } catch (error) {
      toast({
        title: 'Unable to update membership',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  if (!projectId) {
    return (
      <div className="rounded-xl border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">No project selected.</p>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="rounded-xl border p-8 text-center">
        <p className="text-sm text-muted-foreground">Loading project role data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PermissionRequestsCard projectId={projectId} />

      <Card>
        <CardHeader>
          <CardTitle>Project Roles</CardTitle>
          <CardDescription>
            Define the roles available on this project and the permissions they grant. The Admin
            and Basic roles are seeded automatically and cannot be deleted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RolesTable
            roles={roles}
            onCreate={openCreateDialog}
            canCreate
            onView={openDetailsDialog}
            onEdit={openEditDialog}
            onDelete={handleDeleteRole}
          />
        </CardContent>
      </Card>

      <MembershipAssignmentCard
        users={users}
        roles={roles}
        memberships={memberships}
        isSubmitting={upsertMembership.isPending}
        onAssign={handleAssignMembership}
      />

      <Card>
        <CardHeader>
          <CardTitle>Available Permissions</CardTitle>
          <CardDescription>
            Permission codenames are immutable and defined in the backend RBAC catalog. Attach them
            to custom roles via the role editor above.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {permissions.length === 0 ? (
            <div className="rounded-lg border p-3">
              <p className="text-sm text-muted-foreground">No permissions seeded for this project.</p>
            </div>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {permissionGroups.map((group) => (
                <AccordionItem key={group.key} value={group.key}>
                  <AccordionTrigger className="text-sm font-semibold">
                    <span className="flex items-center gap-2">
                      {group.name}
                      <Badge variant="secondary" className="font-normal">
                        {group.permissions.length}
                      </Badge>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pb-3">
                      {group.permissions.map((permission) => (
                        <div key={permission.id} className="rounded-md border p-3">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-medium text-foreground">{permission.label}</p>
                            <code className="font-mono text-xs text-muted-foreground">
                              {permission.codename}
                            </code>
                          </div>
                          {permission.description && (
                            <p className="mt-1 text-sm text-muted-foreground">
                              {permission.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      <RoleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        permissions={permissions}
        role={editingRole}
        isSubmitting={createRole.isPending || updateRole.isPending}
        onSubmit={handleSaveRole}
      />

      <RoleDetailsDialog
        open={detailsDialogOpen}
        onOpenChange={(open) => {
          setDetailsDialogOpen(open);
          if (!open) setViewingRole(null);
        }}
        role={viewingRole}
        permissions={permissions}
      />
    </div>
  );
};
